/**
 * Configurateur AXIO
 * ------------------
 * Logique client : sélection de configuration, aperçu par calques (face/côté),
 * accessoires/lampe optionnels, sous-total en direct, ajout panier multi-produits.
 *
 * Données injectées par sections/configurateur-axio.liquid dans
 * <script type="application/json" data-cfgx-data="<id>">.
 */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (e) {
        /* fallback below */
      }
    }
    var amount = (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
    return amount + ' \u20AC';
  }

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k] != null) node.setAttribute(k, attrs[k]);
      });
    }
    return node;
  }

  function Configurator(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-cfgx-id');
    this.packLabel = root.getAttribute('data-pack-label') || 'Pack';
    this.containsLabel = root.getAttribute('data-msg-contains') || 'Ce pack contient';
    this.incompatibleLabel = root.getAttribute('data-msg-incompatible') || 'Incompatible avec votre sélection';

    var dataNode = root.querySelector('[data-cfgx-data]');
    var data = { configs: [] };
    try {
      if (dataNode) data = JSON.parse(dataNode.textContent) || data;
    } catch (e) {
      console.error('[Configurateur AXIO] JSON invalide', e);
    }
    if (!data || !Array.isArray(data.configs)) data = { configs: [] };
    this.data = data;
    this.moneyFormat = document.body.dataset.moneyFormat || data.moneyFormat || '';

    // Carte d'incompatibilités : productId -> Set des ids incompatibles.
    this.conflicts = {};
    (data.exclusions || []).forEach(function (group) {
      (group || []).forEach(function (id) {
        var key = String(id);
        if (!this.conflicts[key]) this.conflicts[key] = {};
        (group || []).forEach(function (other) {
          if (String(other) !== key) this.conflicts[key][String(other)] = true;
        }, this);
      }, this);
    }, this);

    this.state = {
      configId: null,
      packQty: 1, // quantité de packs choisie
      qty: {}, // productId -> quantité choisie (accessoires + lampes hors pack)
      view: 'face'
    };

    // Raccourcis DOM
    this.dom = {
      configs: root.querySelector('[data-cfgx-configs]'),
      currentName: root.querySelector('[data-cfgx-current-name]'),
      layers: root.querySelector('[data-cfgx-layers]'),
      stage: root.querySelector('[data-cfgx-stage]'),
      thumbs: root.querySelector('[data-cfgx-thumbs]'),
      selection: root.querySelector('[data-cfgx-selection]'),
      accessories: root.querySelector('[data-cfgx-accessories]'),
      lamps: root.querySelector('[data-cfgx-lamps]'),
      lampsPanel: root.querySelector('[data-cfgx-lamps-panel]'),
      packHead: root.querySelector('[data-cfgx-pack-head]'),
      packName: root.querySelector('[data-cfgx-pack-name]'),
      packRef: root.querySelector('[data-cfgx-pack-ref]'),
      packQty: root.querySelector('[data-cfgx-pack-qty]'),
      packInc: root.querySelector('[data-cfgx-pack-inc]'),
      packDec: root.querySelector('[data-cfgx-pack-dec]'),
      total: root.querySelector('[data-cfgx-total]'),
      add: root.querySelector('[data-cfgx-add]'),
      feedback: root.querySelector('[data-cfgx-feedback]'),
      views: root.querySelectorAll('[data-cfgx-view]')
    };

    if (this.data.configs.length) {
      this.bind();
      this.selectConfig(this.data.configs[0].id);
    }
  }

  // Quantité de base d'un composant inclus (depuis « includedQtyRaw »), défaut 1.
  Configurator.prototype.baseQty = function (config, product) {
    if (!config || !product) return 1;
    // Élément manuel : sa quantité de base est portée par l'objet lui-même.
    if (product.manual) return Number(product.manualQty || 1);
    if (!config._qtyMap) {
      var map = {};
      var raw = config.includedQtyRaw || '';
      String(raw).split(/[\r\n]+/).forEach(function (line) {
        var m = line.match(/^\s*(.+?)\s*[:x×=]\s*(\d+)\s*$/);
        if (m) map[m[1].trim().toLowerCase()] = parseInt(m[2], 10);
      });
      config._qtyMap = map;
    }
    var sku = (product.sku || '').toLowerCase();
    if (sku && config._qtyMap[sku] != null) return config._qtyMap[sku];
    return 1;
  };

  // Un produit est-il en conflit avec un produit déjà actif (inclus ou choisi) ?
  Configurator.prototype.isDisabledByConflict = function (product) {
    var conf = this.conflicts[String(product.id)];
    if (!conf) return false;
    var config = this.getConfig();
    if (!config) return false;
    var active = {};
    (config.included || []).forEach(function (p) { active[String(p.id)] = true; });
    this.selectedExtras().forEach(function (e) { active[String(e.product.id)] = true; });
    delete active[String(product.id)];
    return Object.keys(conf).some(function (id) { return active[id]; });
  };

  Configurator.prototype.getConfig = function () {
    var id = this.state.configId;
    return this.data.configs.filter(function (c) { return c.id === id; })[0] || null;
  };

  Configurator.prototype.bind = function () {
    var self = this;

    // Choix de configuration
    if (this.dom.configs) {
      this.dom.configs.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-cfgx-config]');
        if (!btn) return;
        self.selectConfig(btn.getAttribute('data-cfgx-config'));
      });
    }

    // Bascule vue face / côté
    Array.prototype.forEach.call(this.dom.views, function (btn) {
      btn.addEventListener('click', function () {
        self.state.view = btn.getAttribute('data-cfgx-view');
        Array.prototype.forEach.call(self.dom.views, function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        self.renderPreview();
      });
    });

    // Accessoires + lampes : sélecteurs de quantité (− / +)
    [this.dom.accessories, this.dom.lamps].forEach(function (host) {
      if (!host) return;
      host.addEventListener('click', function (e) {
        var dec = e.target.closest('[data-cfgx-qty-dec]');
        var inc = e.target.closest('[data-cfgx-qty-inc]');
        if (dec) { self.changeQty(dec.getAttribute('data-cfgx-qty-dec'), -1); return; }
        if (inc) { self.changeQty(inc.getAttribute('data-cfgx-qty-inc'), 1); return; }
      });
    });

    // Quantité du pack (− / +)
    if (this.dom.packInc) this.dom.packInc.addEventListener('click', function () { self.changePackQty(1); });
    if (this.dom.packDec) this.dom.packDec.addEventListener('click', function () { self.changePackQty(-1); });

    // Panneaux repliables (Votre sélection / Accessoires / Lampes)
    this.root.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-cfgx-panel-toggle]');
      if (!toggle) return;
      var panel = toggle.closest('[data-cfgx-panel]');
      if (!panel) return;
      var open = !panel.classList.contains('is-open');
      panel.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    });

    // Ajout au panier
    if (this.dom.add) {
      this.dom.add.addEventListener('click', function () { self.addToCart(); });
    }
  };

  Configurator.prototype.changeQty = function (pid, delta) {
    var key = String(pid);
    // Empêcher d'ajouter un produit incompatible avec la sélection courante.
    if (delta > 0) {
      var product = this.findOption(key);
      if (product && this.isDisabledByConflict(product)) return;
    }
    var next = (Number(this.state.qty[key] || 0) + delta);
    if (next < 0) next = 0;
    if (next === 0) delete this.state.qty[key];
    else this.state.qty[key] = next;
    this.renderAccessories();
    this.renderLamps();
    this.renderPreview();
    this.renderThumbs();
    this.renderTotal();
  };

  // Retrouve un accessoire/lampe par id dans la config courante.
  Configurator.prototype.findOption = function (pid) {
    var config = this.getConfig();
    if (!config) return null;
    var all = (config.accessories || []).concat(config.lamps || []);
    return all.filter(function (p) { return String(p.id) === String(pid); })[0] || null;
  };

  Configurator.prototype.changePackQty = function (delta) {
    var next = this.state.packQty + delta;
    if (next < 1) next = 1;
    this.state.packQty = next;
    this.renderPackHead();
    this.renderSelection();
    this.renderTotal();
  };

  Configurator.prototype.renderPackHead = function () {
    var config = this.getConfig();
    if (this.dom.packQty) this.dom.packQty.textContent = String(this.state.packQty);
    if (this.dom.packDec) {
      if (this.state.packQty <= 1) this.dom.packDec.setAttribute('disabled', 'disabled');
      else this.dom.packDec.removeAttribute('disabled');
    }
    if (!config) return;
    var packProduct = config.pack;
    if (this.dom.packName) this.dom.packName.textContent = (packProduct && packProduct.title) || config.name || '';
    if (this.dom.packRef) this.dom.packRef.textContent = (packProduct && packProduct.sku) ? ('Réf : ' + packProduct.sku) : '';
  };

  Configurator.prototype.selectConfig = function (configId) {
    this.state.configId = configId;
    this.state.qty = {};
    this.state.packQty = 1;

    // état visuel des vignettes
    if (this.dom.configs) {
      Array.prototype.forEach.call(this.dom.configs.querySelectorAll('[data-cfgx-config]'), function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-cfgx-config') === configId);
      });
    }

    var config = this.getConfig();
    if (this.dom.currentName) this.dom.currentName.textContent = config ? config.name : '';

    this.renderPackHead();
    this.renderSelection();
    this.renderAccessories();
    this.renderLamps();
    this.renderPreview();
    this.renderThumbs();
    this.renderTotal();
    this.setFeedback('', '');
  };

  // ---- Rendus colonne droite ----

  Configurator.prototype.row = function (product, opts) {
    opts = opts || {};
    var qty = Number(this.state.qty[String(product.id)] || 0);
    var disabled = opts.control === 'qty' && this.isDisabledByConflict(product);
    var extraClass = (product.available === false ? ' cfgx__row--unavailable' : '');
    if (opts.included) extraClass += ' cfgx__row--included';
    if (opts.control === 'qty' && qty > 0) extraClass += ' is-selected';
    if (disabled) extraClass += ' cfgx__row--disabled';
    var li = el('li', 'cfgx__row' + extraClass);
    if (disabled) li.setAttribute('title', this.incompatibleLabel);

    if (product.image) {
      li.appendChild(el('img', 'cfgx__row-img', { src: product.image, alt: product.title, loading: 'lazy' }));
    }
    var body = el('div', 'cfgx__row-body');
    var name = el('span', 'cfgx__row-name');
    name.textContent = product.title;
    body.appendChild(name);
    if (product.sku) {
      var ref = el('span', 'cfgx__row-ref');
      ref.textContent = product.sku;
      body.appendChild(ref);
    }
    li.appendChild(body);

    // Le prix n'est affiché que pour les options payantes (accessoires/lampes).
    // Les produits inclus du pack restent purement informatifs (sans prix).
    if (opts.showPrice !== false) {
      var price = el('span', 'cfgx__row-price');
      price.textContent = formatMoney(product.price, this.moneyFormat);
      li.appendChild(price);
    }

    // Produits inclus : quantité (lecture seule) = quantité de base × nb de packs.
    if (opts.included) {
      var qtyTag = el('span', 'cfgx__row-qty');
      qtyTag.textContent = '\u00d7 ' + (opts.displayQty != null ? opts.displayQty : 1);
      li.appendChild(qtyTag);
    }

    if (opts.control === 'qty') {
      var stepper = el('div', 'cfgx__qty');
      var minus = el('button', 'cfgx__qty-btn', { type: 'button', 'data-cfgx-qty-dec': String(product.id), 'aria-label': '-' });
      minus.textContent = '\u2212';
      if (qty <= 0) minus.setAttribute('disabled', 'disabled');
      var val = el('span', 'cfgx__qty-val');
      val.textContent = String(qty);
      var plus = el('button', 'cfgx__qty-btn', { type: 'button', 'data-cfgx-qty-inc': String(product.id), 'aria-label': '+' });
      plus.textContent = '+';
      if (product.available === false || disabled) plus.setAttribute('disabled', 'disabled');
      stepper.appendChild(minus);
      stepper.appendChild(val);
      stepper.appendChild(plus);
      li.appendChild(stepper);
    }
    return li;
  };

  // « Votre sélection » : liste informative des composants du pack, sans prix
  // ni interaction (« ce pack contient ce produit, ce produit… »).
  Configurator.prototype.renderSelection = function () {
    var config = this.getConfig();
    this.dom.selection.innerHTML = '';
    if (!config) return;
    var self = this;
    if (!config.included || !config.included.length) {
      var empty = el('li', 'cfgx__selection-empty');
      empty.textContent = '';
      this.dom.selection.appendChild(empty);
      return;
    }
    // En mode « somme des produits inclus », les composants réels (non manuels)
    // portent le prix : on l'affiche. En mode « pack », ils restent informatifs.
    var sumMode = config.pricingMode === 'sum';
    config.included.forEach(function (p) {
      var display = self.baseQty(config, p) * self.state.packQty;
      var showPrice = sumMode && !p.manual;
      self.dom.selection.appendChild(self.row(p, { control: 'static', included: true, showPrice: showPrice, displayQty: display }));
    });
  };

  Configurator.prototype.renderAccessories = function () {
    var config = this.getConfig();
    this.dom.accessories.innerHTML = '';
    if (!config) return;
    var self = this;
    config.accessories.forEach(function (p) {
      self.dom.accessories.appendChild(self.row(p, { control: 'qty' }));
    });
  };

  Configurator.prototype.renderLamps = function () {
    var config = this.getConfig();
    this.dom.lamps.innerHTML = '';
    var hasLamps = config && config.lamps && config.lamps.length;
    if (this.dom.lampsPanel) this.dom.lampsPanel.hidden = !hasLamps;
    if (!hasLamps) return;
    var self = this;
    config.lamps.forEach(function (p) {
      self.dom.lamps.appendChild(self.row(p, { control: 'qty' }));
    });
  };

  // ---- Sélections courantes ----

  // Options payantes avec quantité (accessoires + lampes hors pack) :
  // renvoie [{ product, quantity }] pour qty >= 1.
  Configurator.prototype.selectedExtras = function () {
    var config = this.getConfig();
    if (!config) return [];
    var self = this;
    var items = [];
    var collect = function (p) {
      var q = Number(self.state.qty[String(p.id)] || 0);
      if (q >= 1) items.push({ product: p, quantity: q });
    };
    (config.accessories || []).forEach(collect);
    (config.lamps || []).forEach(collect);
    return items;
  };

  // Produits affichés dans l'aperçu (calques + vignettes) : le pack, ses
  // composants inclus, puis les options sélectionnées.
  Configurator.prototype.previewProducts = function () {
    var config = this.getConfig();
    if (!config) return [];
    var items = [];
    if (config.pack) items.push(config.pack);
    (config.included || []).forEach(function (p) { items.push(p); });
    this.selectedExtras().forEach(function (e) { items.push(e.product); });
    return items;
  };

  // ---- Aperçu par calques ----

  Configurator.prototype.renderPreview = function () {
    var config = this.getConfig();
    var layersHost = this.dom.layers;
    layersHost.innerHTML = '';
    if (!config) return;

    var view = this.state.view;
    var products = this.previewProducts();

    var layers = products
      .map(function (p) {
        return { src: view === 'side' ? p.layerSide : p.layerFace, order: Number(p.layerOrder || 0) };
      })
      .filter(function (l) { return !!l.src; })
      .sort(function (a, b) { return a.order - b.order; });

    // Fond / scène (sous les calques)
    var scene = view === 'side' ? config.sceneSide : config.sceneFace;
    if (scene) {
      var bg = el('img', 'cfgx__layer', { src: scene, alt: '', 'data-cfgx-scene': '1' });
      bg.style.zIndex = '0';
      layersHost.appendChild(bg);
    }

    if (layers.length) {
      layers.forEach(function (l, i) {
        var img = el('img', 'cfgx__layer', { src: l.src, alt: '', loading: 'eager' });
        img.style.zIndex = String(i + 1);
        layersHost.appendChild(img);
      });
    } else if (!scene) {
      var ph = el('div', 'cfgx__layer');
      ph.style.display = 'flex';
      ph.style.alignItems = 'center';
      ph.style.justifyContent = 'center';
      ph.style.color = '#bbb';
      ph.style.fontSize = '13px';
      ph.textContent = config.name;
      layersHost.appendChild(ph);
    }
  };

  // ---- Vignettes des composants ----

  Configurator.prototype.renderThumbs = function () {
    var host = this.dom.thumbs;
    host.innerHTML = '';
    var products = this.previewProducts();
    products.forEach(function (p) {
      if (!p.image) return;
      var btn = el('button', 'cfgx__thumb', { type: 'button', title: p.title });
      btn.appendChild(el('img', null, { src: p.image, alt: p.title, loading: 'lazy' }));
      host.appendChild(btn);
    });
  };

  // ---- Total ----

  // Sous-total :
  //  - mode « pack » : prix du produit pack × nb de packs (les inclus ne comptent pas) ;
  //  - mode « somme » : somme des produits inclus (prix × qté de base × nb de packs) ;
  //  - dans les deux cas + (prix × quantité) des options (accessoires/lampes).
  Configurator.prototype.renderTotal = function () {
    var config = this.getConfig();
    var self = this;
    var total = 0;
    if (config) {
      if (config.pricingMode === 'sum') {
        (config.included || []).forEach(function (p) {
          if (p.available === false) return;
          total += Number(p.price || 0) * self.baseQty(config, p) * self.state.packQty;
        });
      } else if (config.pack && config.pack.available !== false) {
        total += Number(config.pack.price || 0) * this.state.packQty;
      }
    }
    this.selectedExtras().forEach(function (e) {
      if (e.product.available === false) return;
      total += Number(e.product.price || 0) * e.quantity;
    });
    if (this.dom.total) this.dom.total.textContent = formatMoney(total, this.moneyFormat);
  };

  // ---- Feedback ----

  Configurator.prototype.setFeedback = function (msg, type) {
    if (!this.dom.feedback) return;
    this.dom.feedback.textContent = msg || '';
    this.dom.feedback.className = 'cfgx__feedback' + (type ? ' is-' + type : '');
  };

  // ---- Ajout au panier ----

  Configurator.prototype.addToCart = function () {
    var self = this;
    var config = this.getConfig();
    if (!config) return;

    var properties = {};
    properties[this.packLabel] = config.name;

    // Propriétés de la ligne du pack : on y joint la liste informative des
    // composants inclus (sans prix), pour que le client la voie dans le panier
    // et au checkout, sous la ligne du pack.
    var packProperties = {};
    packProperties[this.packLabel] = config.name;
    var included = config.included || [];
    if (config.pack && included.length) {
      packProperties[this.containsLabel] = included
        .map(function (p) {
          var b = self.baseQty(config, p);
          return p.title + (b > 1 ? ' \u00d7' + b : '');
        })
        .join(' \u00b7 ');
    }

    // Produits ajoutés au panier en tant que pack :
    //  - mode « pack » (produit pack défini) : on ajoute le produit pack avec la
    //    quantité de packs choisie (les composants suivent à l'intérieur) ;
    //  - mode « somme » (ou pas de produit pack) : on ajoute les composants inclus
    //    réels × quantité de packs (chacun devient une ligne de panier) ;
    //  - plus les accessoires et lampes choisis, avec leur quantité.
    var sumMode = config.pricingMode === 'sum';
    var packQty = this.state.packQty;
    var lines = [];
    if (config.pack && !sumMode) {
      lines.push({ product: config.pack, quantity: packQty, properties: packProperties });
    } else {
      included.forEach(function (p) {
        lines.push({ product: p, quantity: self.baseQty(config, p) * packQty, properties: properties });
      });
    }
    this.selectedExtras().forEach(function (e) {
      // Les options hors pack sont des produits classiques : on les ajoute
      // avec leur quantité, en les rattachant au pack via la propriété.
      lines.push({ product: e.product, quantity: e.quantity, properties: properties });
    });

    var items = lines
      .filter(function (l) { return l.product.variantId && l.product.available !== false; })
      .map(function (l) {
        return { id: l.product.variantId, quantity: l.quantity, properties: l.properties };
      });

    if (!items.length) {
      this.setFeedback(this.root.getAttribute('data-msg-empty') || 'Aucun produit disponible.', 'error');
      return;
    }

    this.dom.add.setAttribute('disabled', 'disabled');
    this.setFeedback('', '');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function () {
        self.dom.add.removeAttribute('disabled');
        // Pas de message vert « Ajouté au panier » (doublon avec le bouton) :
        // le tiroir/panier fait déjà office de confirmation. On garde les erreurs.
        self.setFeedback('', '');
        if (window.PXUTheme && window.PXUTheme.jsAjaxCart) {
          try { window.PXUTheme.jsAjaxCart.showDrawer(); } catch (e) {}
          try { window.PXUTheme.jsAjaxCart.updateView(); } catch (e) {}
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        self.dom.add.removeAttribute('disabled');
        var msg = (err && (err.description || err.message)) || (self.root.getAttribute('data-msg-error') || 'Erreur.');
        self.setFeedback(msg, 'error');
        console.error('[Configurateur AXIO] add to cart', err);
      });
  };

  function init() {
    document.querySelectorAll('[data-cfgx]').forEach(function (root) {
      if (root.__cfgxInit) return;
      root.__cfgxInit = true;
      try {
        new Configurator(root);
      } catch (e) {
        console.error('[Configurateur AXIO] init', e);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Réinitialisation dans l'éditeur de thème
  document.addEventListener('shopify:section:load', init);
})();
